import React, { useEffect, useState } from 'react';
import { getSpeedPassOffer, isOfferId } from '../../clients/SpeedPassOfferClient';
import { SpeedPassOffer } from '../../models/speedPass/SpeedPassOffer';
import { offerBannerCopy } from '../../utils/speedPassOffer';

interface OfferBannerProps {
  /** `?offer=` from the email link. Anything that isn't an offer id renders nothing. */
  offerId: string | null;
  /** Test seam: the clock the countdown reads. */
  now?: () => number;
}

const TICK_MS = 30_000;

/**
 * The gold "pay the difference" banner (spec §12). Shows only while Identity says the offer is
 * `active` and it hasn't run out on this clock; every other state, a 404, a 429 or a network
 * failure simply renders nothing.
 */
const OfferBanner: React.FC<OfferBannerProps> = ({ offerId, now = Date.now }) => {
  const [offer, setOffer] = useState<SpeedPassOffer | null>(null);
  const [nowMs, setNowMs] = useState(now);

  useEffect(() => {
    setOffer(null);
    if (!isOfferId(offerId)) return;
    const controller = new AbortController();
    getSpeedPassOffer(offerId, controller.signal).then((result) => {
      if (!controller.signal.aborted) setOffer(result);
    });
    return () => controller.abort();
  }, [offerId]);

  // Keep the countdown honest, and drop the banner the minute the offer runs out.
  useEffect(() => {
    if (!offer) return;
    const id = window.setInterval(() => setNowMs(now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [offer, now]);

  if (!offerId) return null;
  const copy = offerBannerCopy(offerId, offer, nowMs);
  if (!copy) return null;

  return (
    <a className="pl-offer" href={copy.href} data-testid="speed-pass-offer-banner">
      <span className="pl-offer-text">
        <strong>Your {copy.creditText} credit ends in {copy.timeLeftText}.</strong>{' '}
        Turn today into a {copy.passText} for {copy.priceText}.
      </span>
      <span className="pl-offer-cta">Upgrade for {copy.priceText}</span>
    </a>
  );
};

export default OfferBanner;
